import * as React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Slide,
  Stack,
  Chip,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function RemoveItemDialog({
  open,
  onClose,
  onConfirmRemove,
  onMoveToWishlist,
  item,
}) {
  if (!item) return null;

  const itemImage =
    (item?.images && Array.isArray(item.images) && item.images.length > 0
      ? typeof item.images[0] === "string"
        ? item.images[0]
        : item.images[0]?.url
      : null) ||
    item?.image ||
    null;

  const itemPrice = item?.listPrice ?? item?.price ?? 0;
  const itemMRP = item?.maxPrice;
  const itemQty = item?.quantity || 1;

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-labelledby="remove-item-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: {xs: "20px", sm: "24px"},
          p: {xs: 2.5, sm: 3},
          maxWidth: "420px",
          width: "calc(100% - 32px)",
          m: 2,
          boxShadow:
            "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(226, 232, 240, 0.8)",
          position: "relative",
          overflow: "visible",
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(15, 23, 42, 0.55)",
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        size="small"
        aria-label="Close dialog"
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: "#94a3b8",
          bgcolor: "#f8fafc",
          border: "1px solid #f1f5f9",
          "&:hover": {
            color: "#0f172a",
            bgcolor: "#e2e8f0",
          },
        }}
      >
        <CloseRoundedIcon sx={{fontSize: 18}} />
      </IconButton>

      <DialogContent sx={{p: 0}}>
        {/* Header Warning Icon */}
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "16px",
            bgcolor: "#fef2f2",
            border: "1px solid #fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ef4444",
            mb: 2,
          }}
        >
          <DeleteOutlineRoundedIcon sx={{fontSize: 28}} />
        </Box>

        {/* Title & Subtitle */}
        <Typography
          id="remove-item-dialog-title"
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: {xs: "1.15rem", sm: "1.25rem"},
            color: "#0f172a",
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
          }}
        >
          Remove Item from Cart?
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.85rem",
            mt: 0.5,
            lineHeight: 1.5,
          }}
        >
          Are you sure you want to remove this item? You can also move it to your wishlist to buy it later.
        </Typography>

        {/* Item Preview Card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            my: 2.25,
            borderRadius: "14px",
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Thumbnail */}
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "10px",
              bgcolor: "#ffffff",
              border: "1px solid #f1f5f9",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              p: 0.5,
            }}
          >
            {itemImage ? (
              <Box
                component="img"
                src={itemImage}
                alt={item?.title || "Product"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <ShoppingBagOutlinedIcon sx={{color: "#94a3b8", fontSize: 26}} />
            )}
          </Box>

          {/* Details */}
          <Box sx={{minWidth: 0, flexGrow: 1}}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: "0.86rem",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {item?.title || "Selected Item"}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 0.5}}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.92rem",
                  color: "primary.main",
                }}
              >
                &#8377;{itemPrice}
              </Typography>

              {itemMRP && Number(itemMRP) > Number(itemPrice) && (
                <Typography
                  variant="caption"
                  sx={{
                    textDecoration: "line-through",
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  &#8377;{itemMRP}
                </Typography>
              )}

              {itemQty > 1 && (
                <Chip
                  label={`Qty: ${itemQty}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    bgcolor: "#e2e8f0",
                    color: "#475569",
                  }}
                />
              )}
            </Stack>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Stack spacing={1.25}>
          {onMoveToWishlist && (
            <Button
              fullWidth
              variant="outlined"
              onClick={onMoveToWishlist}
              startIcon={<FavoriteBorderRoundedIcon sx={{fontSize: 18}} />}
              sx={{
                py: 1.1,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "0.86rem",
                textTransform: "none",
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  borderColor: "primary.dark",
                  bgcolor: "rgba(47, 93, 126, 0.05)",
                },
              }}
            >
              Move to Wishlist
            </Button>
          )}

          <Stack direction="row" spacing={1.25}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{
                py: 1.1,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "0.86rem",
                textTransform: "none",
                borderColor: "#cbd5e1",
                color: "#475569",
                "&:hover": {
                  borderColor: "#94a3b8",
                  bgcolor: "#f1f5f9",
                  color: "#0f172a",
                },
              }}
            >
              Keep Item
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={onConfirmRemove}
              startIcon={<DeleteOutlineRoundedIcon sx={{fontSize: 18}} />}
              sx={{
                py: 1.1,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "0.86rem",
                textTransform: "none",
                bgcolor: "#dc2626",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(220, 38, 38, 0.25)",
                "&:hover": {
                  bgcolor: "#b91c1c",
                  boxShadow: "0 6px 16px rgba(220, 38, 38, 0.35)",
                },
              }}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
